import React, { useState } from 'react';
import { Loader, Mail } from 'lucide-react';
import { useAuth } from './AuthContext';

export default function Login() {
  const { signIn, verifyOtp, authError, authNotice } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(authError || '');
  const [awaitCode, setAwaitCode] = useState(false);
  const [code, setCode] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [otpSentAt, setOtpSentAt] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    const { error, awaitLink: needsCode } = await signIn(email.trim());
    setLoading(false);
    if (error) {
      const msg = error.message || '';
      if (msg.toLowerCase().includes('email not confirmed')) {
        setError('先に招待メールのリンクをクリックして、アカウントを有効化してください。');
      } else if (msg.toLowerCase().includes('signups not allowed') || msg.toLowerCase().includes('user not found')) {
        setError('このメールアドレスは登録されていません。管理者にお問い合わせください。');
      } else if (msg.toLowerCase().includes('security purposes')) {
        const seconds = msg.match(/(\d+)\s*second/);
        setError(`セキュリティのため、${seconds ? seconds[1] + '秒' : 'しばらく'}お待ちください。`);
      } else {
        setError(msg);
      }
      return;
    }
    if (needsCode) { setAwaitCode(true); setOtpSentAt(Date.now()); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    const { error } = await verifyOtp(email.trim(), code.trim());
    setLoading(false);
    if (error) {
      const elapsed = otpSentAt ? (Date.now() - otpSentAt) / 1000 / 60 : 0;
      if (elapsed >= 10) {
        setError('コードの有効期限が切れました。認証コードを再送信してください。');
        setAwaitCode(false);
        setCode('');
        setAttempts(0);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 3) {
          setError('3回間違えたためコードが無効になりました。認証コードを再送信してください。');
          setAwaitCode(false);
          setCode('');
          setAttempts(0);
        } else {
          setError(`コードが間違っています。正しいコードを入力してください（残り${3 - newAttempts}回）`);
        }
      }
    }
    // 成功時は onAuthStateChange が自動的に session を更新して App へ遷移
  };

  if (awaitCode) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2 whitespace-nowrap">コードを入力してください</h2>
            <p className="text-slate-500 text-sm">
              <span className="font-medium text-slate-700">{email}</span> に<br />
              認証コードを送信しました。<br />
              <span className="text-xs text-slate-400">（有効期限: 10分）</span>
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                認証コード
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                required
                autoFocus
                className="w-full p-3 border border-slate-300 rounded-lg text-sm text-center tracking-widest text-lg font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <><Loader className="w-4 h-4 animate-spin" /> 確認中...</>
              ) : (
                'ログイン'
              )}
            </button>

            <button
              type="button"
              onClick={() => { setAwaitCode(false); setCode(''); setError(''); setEmail(''); setAttempts(0); }}
              className="w-full text-sm text-indigo-600 hover:underline"
            >
              別のメールアドレスを使う
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="logo512.png" alt="" className="w-16 h-16 mx-auto mb-4" />
          <img src="wordmark.png" alt="ProductMatcher" className="h-[26px] mx-auto" />
          <p className="text-slate-500 text-sm mt-3">メールアドレスを入力して認証コードを受け取る</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {authNotice && (
            <p className="text-sm text-indigo-800 bg-indigo-50 p-2 rounded">{authNotice}</p>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              メールアドレス
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@company.com"
              required
              autoFocus
              className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <><Loader className="w-4 h-4 animate-spin" /> 処理中...</>
            ) : (
              '認証コードを送信'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
